import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, TextField, Chip, CircularProgress, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import CompetitorDiscoveryService from '../../services/CompetitorDiscoveryService';
import GoogleAdsKeywordService from '../../services/GoogleAdsKeywordService';
import GMBService from '../../services/GMBService';
import AnalysisLoadingPopup from '../ProfileStrengthResults/components/AnalysisLoadingPopup';

import logger from '../../utils/logger';
const FormCard = styled(Box)(() => ({
  backgroundColor: '#ffffff',
  borderRadius: 24,
  padding: '40px 48px',
  boxShadow: '0 30px 70px rgba(15, 42, 74, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
}));

const TitleBlock = styled(Stack)(() => ({
  gap: 8,
}));




const FormTitle = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: 32,
  fontWeight: 600,
  color: '#0b91d6',
}));

const FormSubtitle = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: 16,
  fontWeight: 400,
  color: '#475467',
}));

const FieldLabel = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  color: '#121927',
  marginBottom: 8,
}));

const StyledSelect = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 18px',
  borderRadius: '8px',
  border: '0.2px solid #A0A0AA',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#0B91D6',
  },
}));

const SelectPlaceholder = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  fontWeight: 400,
  color: '#A0A0AA',
}));

const KeywordChip = styled(Chip)(({ selected }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
  fontWeight: 400,
  height: 32,
  borderRadius: 16,
  paddingInline: 4,
  backgroundColor: selected ? '#34A853' : '#ffffff',
  color: selected ? '#ffffff' : '#121927',
  border: selected ? 'none' : '1px solid #E5E7EB',
  '&:hover': {
    backgroundColor: selected ? '#2E8B47' : '#F9FAFB',
  },
  '& .MuiChip-label': {
    padding: '6px 12px',
  },
}));

const ManualKeywordsField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#A0A0AA',
      borderWidth: '0.2px',
    },
    '&:hover fieldset': {
      borderColor: '#0B91D6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0B91D6',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '16px 18px',
    '&::placeholder': {
      color: '#A0A0AA',
      opacity: 1,
    },
  },
}));

const ContinueButton = styled(Button)(() => ({
  alignSelf: 'flex-start',
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'capitalize',
  padding: '12px 32px',
  borderRadius: 8,
  backgroundColor: '#0B91D6',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#0980C2',
  },
  '&.Mui-disabled': {
    backgroundColor: '#0B91D6',
    opacity: 0.6,
    color: '#ffffff',
  },
}));

const defaultKeywords = [
  'Business services',
  'Local business',
  'Professional services',
  'Customer service',
  'Quality service',
  'Best service',
];

const joinAddress = (storefrontAddress, fallbackAddress) => {
  if (!storefrontAddress) return fallbackAddress || '';
  const parts = [];
  if (Array.isArray(storefrontAddress.addressLines) && storefrontAddress.addressLines.length) {
    parts.push(storefrontAddress.addressLines.join(', '));
  }
  if (storefrontAddress.locality) parts.push(storefrontAddress.locality);
  if (storefrontAddress.administrativeArea) parts.push(storefrontAddress.administrativeArea);
  if (storefrontAddress.postalCode) parts.push(storefrontAddress.postalCode);
  if (storefrontAddress.regionCode) parts.push(storefrontAddress.regionCode);
  return parts.join(', ') || fallbackAddress || '';
};

const extractCityFromAddress = (address) => {
  if (!address) return 'Local Area';
  const parts = address.split(',').map((part) => part.trim());
  const specificAreas = ['zirakpur', 'mohali', 'panchkula', 'sas nagar'];
  const broaderAreas = ['chandigarh', 'delhi', 'mumbai', 'bangalore', 'pune', 'hyderabad', 'gurgaon', 'noida'];

  for (const part of parts) {
    const lower = part.toLowerCase();
    const match = specificAreas.find((area) => lower.includes(area));
    if (match) return match.charAt(0).toUpperCase() + match.slice(1);
  }

  for (const part of parts) {
    const lower = part.toLowerCase();
    const match = broaderAreas.find((area) => lower.includes(area));
    if (match) return match.charAt(0).toUpperCase() + match.slice(1);
  }

  for (const part of parts) {
    const trimmed = part.trim();
    const lower = trimmed.toLowerCase();
    if (
      trimmed.length < 3 ||
      /^\d+/.test(trimmed) ||
      lower.includes('sector') ||
      lower.includes('sco') ||
      lower.includes('plot') ||
      lower.includes('block') ||
      lower.includes('phase') ||
      /^[a-z]-\d+/.test(lower) ||
      /^\d+[a-z]*$/.test(trimmed) ||
      lower.includes('punjab') ||
      lower.includes('haryana') ||
      lower.includes('india')
    ) {
      continue;
    }
    if (trimmed.length > 2) return trimmed;
  }

  return 'Local Area';
};

const deriveStatusForLocation = async (locationId, loc, accessToken) => {
  let simplifiedStatus = 'unknown';
  let hasVoiceOfMerchant = false;
  let hasBusinessAuthority = false;

  try {
    const vom = await GMBService.getVoiceOfMerchantState(locationId, accessToken);
    simplifiedStatus = vom?.simplifiedStatus || 'unknown';
    hasVoiceOfMerchant = vom?.raw?.hasVoiceOfMerchant === true;
    hasBusinessAuthority = vom?.raw?.hasBusinessAuthority === true;
  } catch (error) {
    logger.warn('[ProfileStrengthOnboardingStep] VOM fetch failed for', locationId, error?.message || error);
  }

  const metadata = loc?.metadata || {};
  const locationState = loc?.locationState || {};
  const explicitVerified = metadata.verified === true || locationState.isVerified === true;

  const status = (
    explicitVerified || hasVoiceOfMerchant || hasBusinessAuthority || simplifiedStatus === 'verified'
      ? 'verified'
      : simplifiedStatus === 'suspended'
        ? 'suspended'
        : simplifiedStatus.includes('PENDING')
          ? 'pending_verification'
          : 'unverified'
  );

  return {
    status,
    verificationState: simplifiedStatus,
    verified: status === 'verified',
  };
};

const ProfileStrengthOnboardingStep = ({ buttonLabel = 'Continue', onComplete }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [businessError, setBusinessError] = useState(null);

  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [manualKeywords, setManualKeywords] = useState('');
  const [dynamicKeywords, setDynamicKeywords] = useState(defaultKeywords);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const manualInputRef = useRef(null);

  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        setLoadingBusinesses(true);
        setBusinessError(null);

        const gmbAccounts = await GMBService.getAccounts();
        if (!Array.isArray(gmbAccounts) || gmbAccounts.length === 0) {
          setAccounts([]);
          setSelectedAccount('');
          return;
        }

        const accessToken = await GMBService.getAccessToken();
        const normalized = [];

        for (const account of gmbAccounts) {
          try {
            if (!account?.name) continue;
            const locations = await GMBService.getLocations(accessToken, account.name);
            if (!Array.isArray(locations) || locations.length === 0) continue;

            for (const loc of locations) {
              const id = loc.name?.split('/')?.pop() || loc.locationId || account.name;
              const displayName = loc.title || loc.locationName || loc.name || account.accountName || 'Business';
              const address = joinAddress(loc.storefrontAddress, loc.address || loc.formattedAddress || '');

              const verificationInfo = await deriveStatusForLocation(id, loc, accessToken);

              normalized.push({
                id: loc.name || loc.locationId || displayName,
                name: displayName,
                address,
                status: verificationInfo.status,
                verificationState: verificationInfo.verificationState,
                verified: verificationInfo.verified,
                raw: {
                  ...loc,
                  account,
                },
                accountId: account.name?.split('/')?.[1] || account.accountId,
                locationId: loc.name?.split('/')?.pop() || loc.locationId,
                metadata: loc.metadata || null,
                locationState: loc.locationState || null,
              });
            }
          } catch (error) {
            logger.warn('[ProfileStrengthOnboardingStep] Failed to load locations for account', account?.name, error);
          }
        }

        if (normalized.length === 0) {
          const fallback = gmbAccounts.map((account) => {
            const verificationState = String(account.verificationState || '').toUpperCase();
            let status = 'unverified';
            if (verificationState === 'VERIFIED') status = 'verified';
            else if (verificationState.includes('PENDING')) status = 'pending_verification';
            else if (verificationState === 'SUSPENDED') status = 'suspended';

            return {
              id: account.name?.split('/')?.pop() || account.accountId,
              name: account.accountName || 'Business Account',
              address: 'Account level - no location data',
              status,
              verificationState,
              verified: status === 'verified',
              raw: { account },
              accountId: account.name?.split('/')?.[1] || account.accountId,
              locationId: account.name?.split('/')?.pop() || account.accountId,
              metadata: null,
              locationState: null,
            };
          });

          setAccounts(fallback);
          if (fallback.length && !selectedAccount) {
            setSelectedAccount(fallback[0].name);
          }
          return;
        }

        setAccounts(normalized);
        if (normalized.length && !selectedAccount) {
          setSelectedAccount(normalized[0].name);
        }
      } catch (error) {
        logger.error('[ProfileStrengthOnboardingStep] Failed to load businesses:', error);
        setBusinessError(error.message || 'Unable to fetch businesses');
      } finally {
        setLoadingBusinesses(false);
      }
    };

    loadBusinesses();
  }, []);

  useEffect(() => {
    if (!selectedAccount) {
      setDynamicKeywords(defaultKeywords);
      return;
    }

    const fetchDynamicKeywords = async () => {
      try {
        setKeywordsLoading(true);
        const selectedBiz = accounts.find((a) => a.name === selectedAccount);
        const businessData = selectedBiz?.raw;
        if (!businessData) {
          setDynamicKeywords(defaultKeywords);
          return;
        }

        const businessName = selectedBiz?.name || businessData?.title || 'Business';
        const businessAddress = selectedBiz?.address || joinAddress(
          businessData?.storefrontAddress,
          businessData?.address || businessData?.formattedAddress || ''
        );

        const locationData = {
          title: businessName,
          name: businessName,
          categories: {
            primaryCategory: {
              displayName: businessName,
            },
          },
          storefrontAddress: businessAddress
            ? { locality: extractCityFromAddress(businessAddress) }
            : { locality: 'Local Area' },
        };

        const businessCategory = GoogleAdsKeywordService.extractBusinessCategory(locationData);
        
        // Get real keywords from DataForSEO API (India only)
        const keywords = await GoogleAdsKeywordService.getKeywordSuggestions({
          businessCategory,
          location: 'India'
        });
        const keywordTerms = keywords.slice(0, 6).map((k) => k.searchTerm);
        setDynamicKeywords(keywordTerms.length ? keywordTerms : defaultKeywords);
      } catch (error) {
        logger.error('[ProfileStrengthOnboardingStep] Error fetching dynamic keywords:', error);
        setDynamicKeywords(defaultKeywords);
      } finally {
        setKeywordsLoading(false);
      }
    };

    fetchDynamicKeywords();
  }, [selectedAccount, accounts]);

  const handleKeywordToggle = (keyword) => {
    setSelectedKeywords((prev) => {
      if (prev.includes(keyword)) {
        return prev.filter((k) => k !== keyword);
      }
      if (prev.length >= 3) {
        const [, ...rest] = prev;
        return [...rest, keyword];
      }
      return [...prev, keyword];
    });
    if (manualInputRef.current) {
      manualInputRef.current.focus();
    }
  };

  const computeAddress = (rawBiz, selectedBiz) => {
    if (!rawBiz && !selectedBiz) return '';
    return joinAddress(
      rawBiz?.storefrontAddress || selectedBiz?.raw?.storefrontAddress,
      rawBiz?.address || rawBiz?.formattedAddress || selectedBiz?.address || ''
    );
  };

  const handleContinue = async () => {
    const selectedBiz = accounts.find((a) => a.name === selectedAccount) || null;
    if (!selectedBiz) {
      alert('Please select an account first');
      return;
    }

    setIsAnalyzing(true);

    const raw = selectedBiz?.raw || {};
    const manualParts = manualKeywords.split(',').map((k) => k.trim()).filter(Boolean);
    const keywords = [...selectedKeywords, ...manualParts].slice(0, 3);
    const address = computeAddress(raw, selectedBiz);

    try {
      let latitude = raw?.latlng?.latitude;
      let longitude = raw?.latlng?.longitude;
      const placeId = raw?.id || raw?.place_id;

      if (!latitude || !longitude) {
        if (placeId) {
          try {
            const placeDetails = await CompetitorDiscoveryService.getPlaceDetails(placeId);
            if (placeDetails?.geometry?.location) {
              latitude = placeDetails.geometry.location.lat;
              longitude = placeDetails.geometry.location.lng;
            }
          } catch (error) {
            logger.warn('[ProfileStrengthOnboardingStep] Place details lookup failed', error?.message || error);
          }
        }

        if ((!latitude || !longitude) && raw?.address) {
          try {
            const geocodeResult = await CompetitorDiscoveryService.geocodeAddress(raw.address);
            if (geocodeResult?.lat && geocodeResult?.lng) {
              latitude = geocodeResult.lat;
              longitude = geocodeResult.lng;
            }
          } catch (error) {
            logger.warn('[ProfileStrengthOnboardingStep] Geocode lookup failed', error?.message || error);
          }
        }

        if (!latitude || !longitude) {
          latitude = 28.6139; // Default to Delhi, India
          longitude = 77.2090;
        }
      }

      const allKeywords = [...selectedKeywords, ...manualParts].slice(0, 3);
      const primaryKeyword = allKeywords[0];

      if (latitude && longitude && primaryKeyword) {
        const competitors = await CompetitorDiscoveryService.findCompetitors({
          latitude,
          longitude,
          keyword: primaryKeyword,
          excludePlaceId: placeId,
        });

        if (typeof onComplete === 'function') {
          onComplete({ business: selectedBiz, keywords, address });
        }

        navigate('/profile-strength-results', {
          state: {
            business: selectedBiz,
            keywords,
            address,
            competitors,
            selected: raw,
            startAnalysis: true,
            businesses: accounts,
            redirectToDashboard: true,
            triggeredFromOnboarding: true,
          },
        });
        return;
      }
    } catch (error) {
      logger.warn('[ProfileStrengthOnboardingStep] Competitor discovery failed, falling back', error);
    }

    if (typeof onComplete === 'function') {
      onComplete({ business: selectedBiz, keywords, address });
    }

    navigate('/profile-strength-results', {
      state: {
        business: selectedBiz,
        keywords,
        address,
        competitors: [],
        selected: raw,
        startAnalysis: true,
        businesses: accounts,
        redirectToDashboard: true,
        triggeredFromOnboarding: true,
      },
    });
  };

  return (
    <>
      <AnalysisLoadingPopup open={isAnalyzing} />
      <FormCard>
        <TitleBlock>
          <FormTitle>Just one step</FormTitle>
          <FormSubtitle>
            Let's analyse the strength of your profile. Provide us with a few details to tailor your experience.
          </FormSubtitle>
        </TitleBlock>

      {loadingBusinesses ? (
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress size={32} />
          <Typography color="textSecondary">Loading your connected Google Business profiles...</Typography>
        </Stack>
      ) : businessError ? (
        <Typography color="error" variant="body2">
          {businessError}
        </Typography>
      ) : accounts.length === 0 ? (
        <Typography color="textSecondary" variant="body2">
          No connected Google Business profiles found. Please connect an account first.
        </Typography>
      ) : (
        <Stack spacing={3}>
          <Stack spacing={3}>
            <Box>
              <FieldLabel>Select Account*</FieldLabel>
              <Box sx={{ position: 'relative' }}>
                <StyledSelect onClick={() => setAccountMenuOpen((prev) => !prev)}>
                  <SelectPlaceholder>{selectedAccount || 'Select account'}</SelectPlaceholder>
                </StyledSelect>
                {accountMenuOpen && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '52px',
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      zIndex: 10,
                      maxHeight: 260,
                      overflowY: 'auto',
                    }}
                  >
                    {accounts.map((acc) => (
                      <Box
                        key={acc.id}
                        sx={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#F9FAFB' },
                        }}
                        onClick={() => {
                          setSelectedAccount(acc.name);
                          setAccountMenuOpen(false);
                        }}
                      >
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 500 }}>
                          {acc.name}
                        </Typography>
                        {acc.address && (
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#6b7280' }}>
                            {acc.address}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <Box>
              <FieldLabel>Select Your Keywords* (Select up to three)</FieldLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                {keywordsLoading ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">
                      Loading business-specific keywords...
                    </Typography>
                  </Stack>
                ) : (
                  dynamicKeywords.map((keyword) => (
                    <KeywordChip
                      key={keyword}
                      label={keyword}
                      selected={selectedKeywords.includes(keyword)}
                      onClick={() => handleKeywordToggle(keyword)}
                      clickable
                    />
                  ))
                )}
              </Box>
            </Box>

            <Box>
              <FieldLabel>Write Keywords Manually</FieldLabel>
              <ManualKeywordsField
                placeholder="Enter keywords"
                fullWidth
                value={manualKeywords}
                onChange={(event) => setManualKeywords(event.target.value)}
                inputRef={manualInputRef}
              />
            </Box>
          </Stack>

          <ContinueButton onClick={handleContinue} disabled={isAnalyzing}>{isAnalyzing ? 'Analyzing...' : buttonLabel}</ContinueButton>
        </Stack>
      )}
      </FormCard>
    </>
  );
};

export default ProfileStrengthOnboardingStep;












